import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useSelector } from 'react-redux';
import { selectUserParams } from '../../../../store/slices/userParamsSlice';
import { usePermissions } from '../../../../hooks/usePermissions';
import SapMeinsDropdown from '../../../../components/common/SapMeinsDropdown';
import SapCurrencyDropdown from '../../../../components/common/SapCurrencyDropdown';
import toastService from '../../../../utils/toastService';

const ExternalItemDescriptionForm = ({
    visible,
    onHide,
    onSubmit,
    initialData = null,
    isLoading = false
}) => {
    const { th } = useLanguage();
    const userParams = useSelector(selectUserParams);
    const { hasDealerRole, hasServiceDealerRole, hasSalesDealerRole } = usePermissions();
    const isEdit = !!initialData;
    
    // Dealer kontrolü
    const isDealer = hasDealerRole() || hasServiceDealerRole() || hasSalesDealerRole();

    const [submitted, setSubmitted] = useState(false);
    const [formData, setFormData] = useState({
        partCode: '',
        partName: '',
        originalPartName: '',
        meins: null,
        salesPrice: null,
        currency: null,
        isActive: 1
    });


    useEffect(() => {
        if (visible) {
            if (initialData) {
                setFormData({
                    partCode: initialData.partCode || '',
                    partName: initialData.partName || '',
                    originalPartName: initialData.originalPartName || '',
                    meins: initialData.meins || null,
                    salesPrice: initialData.salesPrice || null,
                    currency: initialData.currency || null,
                    isActive: initialData.isActive !== undefined ? initialData.isActive : 1
                });
            } else {
                setFormData({
                    partCode: '',
                    partName: '',
                    originalPartName: '',
                    meins: null,
                    salesPrice: null,
                    currency: null,
                    isActive: 1
                });
            }
            setSubmitted(false);
        }
    }, [visible, initialData]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const validateForm = () => {
        const errors = {};
        
        if (!formData.partCode?.trim()) {
            errors.partCode = th('externalItemDescription.validation.partCodeRequired') || 'Parça Kodu zorunludur';
        }
        
        if (!formData.partName?.trim()) {
            errors.partName = th('externalItemDescription.validation.partNameRequired') || 'Parça Adı zorunludur';
        }

        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        setSubmitted(true);
        
        if (!validateForm()) {
            toastService.error(th('externalItemDescription.validation.formInvalid') || 'Lütfen zorunlu alanları doldurun');
            return;
        }

        const payload = {
            partCode: formData.partCode.trim(),
            partName: formData.partName.trim(),
            originalPartName: formData.originalPartName?.trim() || null,
            meins: formData.meins || null,
            salesPrice: formData.salesPrice || null,
            currency: formData.currency || null,
            bosBranchCode: userParams.dealerId?.toString() || null,
            bosBranchSiteCode: userParams.siteUseId?.toString() || null,
            isActive: formData.isActive
        };

        // Update için id'yi body'ye ekle
        if (initialData?.id) {
            payload.id = initialData.id;
        }

        try {
            await onSubmit(payload);
            onHide();
        } catch (error) {
            console.error('Form submit error:', error);
        }
    };

    const renderFooter = () => {
        return (
            <div className="flex justify-content-end gap-2">
                <Button
                    label={th('common.cancel') || 'Vazgeç'}
                    icon="pi pi-times"
                    onClick={onHide}
                    className="p-button-secondary"
                    disabled={isLoading}
                />
                <Button
                    label={th('common.save') || 'Kaydet'}
                    icon="pi pi-check"
                    onClick={handleSubmit}
                    loading={isLoading}
                />
            </div>
        );
    };

    return (
        <Dialog
            header={initialData ? (th('externalItemDescription.editTitle') || 'Kalem Düzenle') : (th('externalItemDescription.createTitle') || 'Yeni Kalem Ekle')}
            visible={visible}
            style={{ width: '50vw' }}
            footer={renderFooter()}
            onHide={onHide}
            modal
            className="p-fluid"
        >
            <div className="grid">
                <div className="col-12">
                    <label htmlFor="partCode" className="block text-900 font-medium mb-2">
                        {th('externalItemDescription.partCode') || 'Parça Kodu'} <span className="text-red-500">*</span>
                    </label>
                    <InputText
                        id="partCode"
                        value={formData.partCode}
                        onChange={(e) => handleChange('partCode', e.target.value)}
                        className={submitted && !formData.partCode?.trim() ? 'p-invalid' : ''}
                        disabled={isLoading || isEdit} 
                    />
                    {submitted && !formData.partCode?.trim() && (
                        <small className="p-error block mt-1">
                            {th('externalItemDescription.validation.partCodeRequired') || 'Parça Kodu zorunludur'}
                        </small>
                    )}
                </div>

                <div className="col-12">
                    <label htmlFor="partName" className="block text-900 font-medium mb-2">
                        {th('externalItemDescription.partName') || 'Parça Adı'} <span className="text-red-500">*</span>
                    </label>
                    <InputText
                        id="partName"
                        value={formData.partName}
                        onChange={(e) => handleChange('partName', e.target.value)}
                        className={submitted && !formData.partName?.trim() ? 'p-invalid' : ''}
                        disabled={isLoading || isEdit}
                    />
                    {submitted && !formData.partName?.trim() && (
                        <small className="p-error block mt-1">
                            {th('externalItemDescription.validation.partNameRequired') || 'Parça Adı zorunludur'}
                        </small>
                    )}
                </div>

                <div className="col-12">
                    <label htmlFor="originalPartName" className="block text-900 font-medium mb-2">
                        {th('externalItemDescription.originalPartName') || 'Orijinal Adı'}
                    </label>
                    <InputText
                        id="originalPartName"
                        value={formData.originalPartName}
                        onChange={(e) => handleChange('originalPartName', e.target.value)}
                        disabled={isLoading || isEdit}
                    />
                </div>

                <div className="col-12 md:col-6">
                    <label htmlFor="meins" className="block text-900 font-medium mb-2">
                        {th('externalItemDescription.meins') || 'Ölçü Birimi'}
                    </label>
                    <SapMeinsDropdown
                        value={formData.meins}
                        onChange={(e) => handleChange('meins', e.value)}
                        className="w-full"
                        disabled={isLoading || isEdit}
                    />
                </div>

                <div className="col-12 md:col-6">
                    <label htmlFor="currency" className="block text-900 font-medium mb-2">
                        {th('externalItemDescription.currency') || 'Para Birimi'}
                    </label>
                    <SapCurrencyDropdown
                        value={formData.currency}
                        onChange={(e) => handleChange('currency', e.value)}
                        className="w-full"
                        disabled={isLoading}
                        isDealer={isDealer}
                    />
                </div>

                <div className="col-12">
                    <label htmlFor="salesPrice" className="block text-900 font-medium mb-2">
                        {th('externalItemDescription.salesPrice') || 'Satış Fiyatı'}
                    </label>
                    <InputNumber
                        id="salesPrice"
                        value={formData.salesPrice}
                        onValueChange={(e) => handleChange('salesPrice', e.value)}
                        mode="decimal"
                        minFractionDigits={2}
                        maxFractionDigits={2}
                        className="w-full"
                        disabled={isLoading}
                    />
                </div>

                <div className="col-12">
                    <div className="flex align-items-center">
                        <Checkbox
                            inputId="isActive"
                            checked={formData.isActive === 1}
                            onChange={(e) => handleChange('isActive', e.checked ? 1 : 0)}
                            disabled={isLoading}
                        />
                        <label htmlFor="isActive" className="ml-2">
                            {th('common.active') || 'Aktif'}
                        </label>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default ExternalItemDescriptionForm;

